import { isNil, pick } from 'lodash';
import moment from 'moment';

import { getOrganizationProfileType, isBrandsFocusedOrganization } from './organization';

import { logError } from 'utils/log';
import { TEMP_UID_PREFIX } from 'utils/id';

import type { MessengerGroupedMessages, MessengerMessage, MessengerMessagesMeta, State, Subcampaign } from 'types';

export const MESSAGE_TYPE_BRAND = 'brand';
export const MESSAGE_TYPE_INFLUENCER = 'influencer';

const ICONS_BY_MIMETYPE = {
  'application/pdf': 'file-type-pdf',
  'image/png': 'file-type-image',
  'image/jpeg': 'file-type-image',
  'image/pjpeg': 'file-type-image',
  'image/webp': 'file-type-image',
  'image/gif': 'file-type-image',
  'application/mp4': 'file-type-video',
  'video/3gpp': 'file-type-video',
  'video/3gpp2': 'file-type-video',
  'video/x-msvideo': 'file-type-video',
  'video/mp4': 'file-type-video',
  'video/quicktime': 'file-type-video',
  'video/ogg': 'file-type-video',
  'video/webm': 'file-type-video',
  'video/x-flv': 'file-type-video',
  'video/x-ms-wmv': 'file-type-video',
  'video/mpeg': 'file-type-video',
  'video/avi': 'file-type-video'
};

export function getMessagesGroupingTitle(message: MessengerMessage): string {
  return moment(message.createdAt).format('YYYYMMDD');
}

export function isTempMessage(message: MessengerMessage): boolean {
  return message.externalId.startsWith(TEMP_UID_PREFIX);
}

// @TODO: check if addToEnd is actually needed, currently it looks confusing accompanied by messages.reverse()
export function groupMessagesByDate(
  messages: Array<MessengerMessage>,
  groupedMessages: { [key: string]: MessengerGroupedMessages } = {},
  addToEnd: boolean = false
): { [key: string]: MessengerGroupedMessages } {
  if (messages.length === 0) {
    return groupedMessages;
  }

  const newGroupedMessages = {
    ...groupedMessages
  };

  [...messages].reverse().forEach(message => {
    const date = getMessagesGroupingTitle(message);

    if (newGroupedMessages[date]) {
      let updatedMessages;

      if (addToEnd) {
        updatedMessages = [
          ...newGroupedMessages[date].messages,
          message
        ];
      } else {
        updatedMessages = [
          message,
          ...newGroupedMessages[date].messages
        ];
      }

      newGroupedMessages[date] = {
        groupKey: date,
        messages: updatedMessages
      };
    } else {
      newGroupedMessages[date] = {
        groupKey: date,
        messages: [message]
      };
    }
  });

  return newGroupedMessages;
}

export function getAttachmentIcon(
  attachment: {
    mimeType: string
  }
) {
  return ICONS_BY_MIMETYPE[attachment.mimeType] || 'file-type-default';
}

function isEqualMap(mapA, mapB) {
  let equal = true;

  Object.keys(mapA).every((id) => {
    if (isNil(mapB[id])) {
      equal = false;
      return false; // break loop
    }

    return true;
  });

  return equal;
}

export function compareAndMergeMeta(
  currentMeta: MessengerMessagesMeta,
  newMeta: MessengerMessagesMeta
): MessengerMessagesMeta {
  // check organizations
  const currentOrganizations = currentMeta.organizations;
  const newOrganizations = newMeta.organizations;
  let mergedOrganizations;

  if (
    Object.keys(currentOrganizations).length !== Object.keys(newOrganizations).length ||
    !isEqualMap(currentOrganizations, newOrganizations)
  ) { // merge it
    mergedOrganizations = {
      ...currentOrganizations,
      ...newOrganizations
    };
  }

  // check users
  const currentUsers = currentMeta.users;
  const newUsers = newMeta.users;
  let mergedUsers;

  if (
    Object.keys(currentUsers).length !== Object.keys(newUsers).length ||
    !isEqualMap(currentUsers, newUsers)
  ) { // merge it
    mergedUsers = {
      ...currentUsers,
      ...newUsers
    };
  }

  if (isNil(mergedUsers) && isNil(mergedOrganizations)) {
    return currentMeta;
  }

  return {
    organizations: isNil(mergedOrganizations) ? currentOrganizations : mergedOrganizations,
    users: isNil(mergedUsers) ? currentUsers : mergedUsers
  };
}

/*
 * This function will return a custom metadata with the current state,
 * it's super useful when we create a new message so we don't
 * need to as backend for this information again.
 */
export function getCustomMetadataByState(state: State): MessengerMessagesMeta {
  const currentSubcampaign = state.subcampaign.data;
  const { currentOrganization } = state.organizations;
  const { profile: brandProfile } = state.brandProfile;
  const { currentAccount } = state.account;

  if (isNil(currentAccount) || isNil(currentSubcampaign) || isNil(currentOrganization)) {
    // broken state, we should always have current account and current subcampaign for messenger
    logError('Missed messenger state data');

    return {
      organizations: {},
      users: {}
    };
  }

  let currentProfile;
  const organizationProfileType = getOrganizationProfileType(currentOrganization);

  // this logic is to prevent to go to backend to get the specific data and meta
  if (isBrandsFocusedOrganization(currentOrganization)) { // I need brand profile
    currentProfile = brandProfile;
  } else { // I need influencer profile
    currentProfile = currentSubcampaign.creatorProfile;
  }

  let organizations = {};
  // TypeScript seems to be struggling with dynamic types
  if (organizationProfileType === 'brand') {
    organizations = {
      [currentOrganization.id]: {
        ...pick(currentOrganization, ['name', 'id']),
        brandProfile: currentProfile
      }
    };
  }

  if (organizationProfileType === 'influencer') {
    organizations = {
      [currentOrganization.id]: {
        ...pick(currentOrganization, ['name', 'id']),
        influencerProfile: currentProfile
      }
    };
  }

  return {
    organizations,
    users: {
      [currentAccount.uuid]: {
        ...pick(currentAccount, ['firstName', 'id', 'lastName', 'uuid']),
        organizations: currentAccount.organizations.map(o => o.id)
      }
    }
  };
}

export function shouldMessengerBeDisabled(subcampaign: Subcampaign): boolean {
  const influencerProfile = subcampaign ? subcampaign.creatorProfile : null;
  const approvalStatus = influencerProfile?.approvalStatus ?? null;

  return approvalStatus === 'pending' ||
    approvalStatus === 'rejected' ||
    Boolean(influencerProfile?.isLightweight);
}

export function isOfferUpdateSystemMessage(message?: MessengerMessage): boolean {
  if (isNil(message)) {
    return false;
  }

  const { type, subcampaign } = message.metadata;

  return Boolean(type === 'NEGOTIATION_REOPEN' && subcampaign && (subcampaign.offer || subcampaign.updatedOffer));
}
